import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useServices, useCreateService } from '@/hooks/useServices'
import { useNodes } from '@/hooks/useNodes'
import type { Node, Service } from '@/api/types'
import {
  Badge,
  Button,
  Card,
  EmptyState,
  FormRow,
  Input,
  Select,
  Table,
} from '@/admin-design/_common'
import type { BadgeVariant } from '@/admin-design/_common'

const stateVariant: Record<string, BadgeVariant> = {
  running: 'success',
  exited: 'destructive',
  not_found: 'muted',
  registered: 'muted',
}

export default function Services() {
  const { data: services, isLoading, error } = useServices()
  const { data: nodes } = useNodes()
  const [showModal, setShowModal] = useState(false)
  const nav = useNavigate()

  // Group services by node_id
  const nodeMap = new Map<number, Node>()
  nodes?.forEach((n) => nodeMap.set(n.id, n))

  const grouped = new Map<string, { node: Node | null; services: Service[] }>()
  if (services) {
    for (const svc of services) {
      const node = nodeMap.get(svc.node_id) ?? null
      const key = node?.name ?? `node-${svc.node_id}`
      if (!grouped.has(key)) {
        grouped.set(key, { node, services: [] })
      }
      grouped.get(key)!.services.push(svc)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-foreground">Services</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {services?.length ?? 0} services across {grouped.size} nodes
          </p>
        </div>
        <Button type="button" variant="primary" size="sm" onClick={() => setShowModal(true)}>
          Add Service
        </Button>
      </div>

      {error && (
        <div className="bg-destructive-soft text-destructive-soft-foreground px-4 py-3 rounded border border-destructive-soft text-sm">
          Failed to load services: {(error as Error).message}
        </div>
      )}

      {isLoading && (
        <div className="space-y-4">
          {[1, 2].map((i) => (
            <Card key={i} className="p-6 animate-pulse">
              <div className="h-5 bg-muted rounded w-32 mb-4" />
              <div className="space-y-2">
                {[1, 2, 3].map((j) => (
                  <div key={j} className="h-10 bg-muted rounded" />
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}

      {!isLoading && [...grouped.entries()].map(([key, { node, services: svcs }]) => (
        <Card key={key} className="overflow-hidden">
          <div className="px-4 py-3 bg-muted/50 border-b border-border flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${node?.status === 'online' ? 'bg-success' : 'bg-muted-foreground/40'}`} />
            <h2 className="font-semibold text-sm text-card-foreground">
              {node?.name ?? key}
            </h2>
            <span className="text-xs text-muted-foreground">
              {node?.host ?? ''} — {svcs.length} services
            </span>
          </div>
          {/* Mobile: stacked cards (easier to tap than a cramped wide table).
              Desktop: the original table. Same data both ways, guarded by
              sm: breakpoints so there's no duplicate DOM at any viewport. */}
          <div className="sm:hidden divide-y divide-border">
            {svcs.map((svc) => (
              <button
                key={svc.id}
                type="button"
                onClick={() => nav(`/services/${svc.id}`)}
                className="w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-sm text-card-foreground break-all">
                        {svc.name}
                      </span>
                      {svc.is_custom && (
                        <Badge variant="info" className="text-[10px] rounded shrink-0">custom</Badge>
                      )}
                    </div>
                    <div className="mt-0.5 font-mono text-[11px] text-muted-foreground truncate">
                      {svc.image.split('/').pop()}
                    </div>
                  </div>
                  <Badge variant={stateVariant[svc.container_state] ?? 'muted'} className="rounded shrink-0">
                    {svc.container_state === 'not_found' ? 'not running' : svc.container_state}
                  </Badge>
                </div>
                <div className="mt-1.5 flex items-center gap-2 flex-wrap text-[11px] text-muted-foreground">
                  {svc.domain ? (
                    <a
                      href={`https://${svc.domain}`}
                      target="_blank"
                      rel="noreferrer"
                      onClick={(e) => e.stopPropagation()}
                      className="text-info hover:underline break-all"
                    >
                      {svc.domain}
                    </a>
                  ) : (
                    <span>no domain</span>
                  )}
                  {svc.container_network && (
                    <>
                      <span className="opacity-40">·</span>
                      <span className="font-mono">{svc.container_network}</span>
                    </>
                  )}
                  {svc.last_deploy && (
                    <>
                      <span className="opacity-40">·</span>
                      <span className="font-mono">{svc.last_deploy.commit_hash?.slice(0, 7)}</span>
                    </>
                  )}
                </div>
              </button>
            ))}
          </div>
          <div className="hidden sm:block overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.Row className="hover:bg-transparent">
                <Table.Header className="px-4">Name</Table.Header>
                <Table.Header className="px-4">Image</Table.Header>
                <Table.Header className="px-4">Domain</Table.Header>
                <Table.Header className="px-4">Container</Table.Header>
                <Table.Header className="px-4">Network</Table.Header>
                <Table.Header className="px-4">Last Deploy</Table.Header>
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {svcs.map((svc) => (
                <Table.Row
                  key={svc.id}
                  className="cursor-pointer"
                  onClick={() => nav(`/services/${svc.id}`)}
                >
                  <Table.Cell className="px-4 py-3 font-medium text-card-foreground">
                    {svc.name}
                    {svc.is_custom && (
                      <Badge variant="info" className="ml-2 text-[10px] rounded">custom</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 font-mono text-xs text-muted-foreground truncate max-w-[180px]">
                    {svc.image.split('/').pop()}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-muted-foreground text-xs">
                    {svc.domain ? (
                      <a
                        href={`https://${svc.domain}`}
                        target="_blank"
                        rel="noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="text-info hover:underline"
                      >
                        {svc.domain}
                      </a>
                    ) : '-'}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3">
                    <Badge variant={stateVariant[svc.container_state] ?? 'muted'} className="rounded">
                      {svc.container_state === 'not_found' ? 'not running' : svc.container_state}
                    </Badge>
                    {svc.container_status && svc.container_state === 'running' && (
                      <span className="ml-2 text-xs text-muted-foreground">{svc.container_status}</span>
                    )}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-xs font-mono text-muted-foreground">
                    {svc.container_network || '-'}
                  </Table.Cell>
                  <Table.Cell className="px-4 py-3 text-xs text-muted-foreground">
                    {svc.last_deploy
                      ? `${svc.last_deploy.commit_hash?.slice(0, 7) ?? ''} · ${new Date(svc.last_deploy.started_at * 1000).toLocaleDateString('ko-KR')}`
                      : '-'}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
          </div>
        </Card>
      ))}

      {!isLoading && services?.length === 0 && (
        <Card>
          <EmptyState
            title="No services registered yet"
            description="Click &quot;Add Service&quot; to register the first service."
          />
        </Card>
      )}

      {showModal && <AddServiceModal onClose={() => setShowModal(false)} />}
    </div>
  )
}

function AddServiceModal({ onClose }: { onClose: () => void }) {
  const { data: nodes } = useNodes()
  const create = useCreateService()
  const [form, setForm] = useState({
    name: '',
    node_id: 0,
    container_port: 8080,
    image: '',
    domain: '',
    env: '{}',
    volumes: '[]',
  })

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    let envParsed: Record<string, string> = {}
    let volParsed: string[] = []
    try { envParsed = JSON.parse(form.env) } catch { /* ignore */ }
    try { volParsed = JSON.parse(form.volumes) } catch { /* ignore */ }

    create.mutate(
      {
        name: form.name,
        node_id: form.node_id,
        container_port: form.container_port,
        image: form.image,
        domain: form.domain,
        env: envParsed,
        volumes: volParsed,
      } as never,
      { onSuccess: onClose },
    )
  }

  const nodeOptions = (nodes ?? []).map((n) => ({
    value: String(n.id),
    label: `${n.name} (${n.host})`,
  }))

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md mx-4 shadow-lg max-h-[90vh] overflow-y-auto">
        <form onSubmit={onSubmit} className="p-6 space-y-4">
          <h2 className="text-lg font-bold text-card-foreground">Add Service</h2>

          {/* All rows stacked — the default 2-column FormRow grid starves
              the inputs inside a max-w-md modal. */}
          <FormRow label="Name" required stacked>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </FormRow>

          <FormRow label="Node" required stacked>
            <Select
              required
              value={String(form.node_id || '')}
              onValueChange={(v) => setForm({ ...form, node_id: Number(v) })}
              options={nodeOptions}
              placeholder="Select node..."
            />
          </FormRow>

          <FormRow
            label="Image"
            required
            stacked
            help="Public Docker Hub images (nginx:1.25, redis:7-alpine, bitnami/postgresql:16) and private ghcr.io images both work. Private registries need credentials in Settings → Registry."
          >
            <Input
              required
              placeholder="ghcr.io/pentarab/my-app:latest  ·  redis:7-alpine"
              className="font-mono"
              value={form.image}
              onChange={(e) => setForm({ ...form, image: e.target.value })}
            />
          </FormRow>

          <div className="grid grid-cols-2 gap-4">
            <FormRow label="Port" stacked>
              <Input
                type="number"
                value={form.container_port}
                onChange={(e) => setForm({ ...form, container_port: Number(e.target.value) })}
              />
            </FormRow>
            <FormRow label="Domain" stacked>
              <Input
                placeholder="app.pentarab.com"
                value={form.domain}
                onChange={(e) => setForm({ ...form, domain: e.target.value })}
              />
            </FormRow>
          </div>

          <FormRow label="Environment Variables (JSON)" stacked>
            <textarea
              rows={3}
              className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              value={form.env}
              onChange={(e) => setForm({ ...form, env: e.target.value })}
            />
          </FormRow>

          <FormRow label="Volumes (JSON array)" stacked>
            <textarea
              rows={2}
              className="w-full rounded-md border border-border bg-card px-3 py-1.5 text-sm font-mono text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-1 focus:ring-ring"
              value={form.volumes}
              onChange={(e) => setForm({ ...form, volumes: e.target.value })}
            />
          </FormRow>

          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="primary" size="sm" loading={create.isPending}>
              {create.isPending ? 'Creating...' : 'Create'}
            </Button>
          </div>
        </form>
      </Card>
    </div>
  )
}
